/**
 * AuthContext - React context for authentication state management
 * LAYER-IDENTITY-ACCESS
 *
 * Integrates WorkOS AuthKit for authentication with our existing RBAC system.
 * WorkOS handles: login, logout, session management, Google OAuth.
 * We handle: role-based access control, permissions, user data from our DB.
 */
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { AuthKitProvider, useAuth as useWorkOSAuth } from '@workos-inc/authkit-react';
import { useQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import {
  AuthState,
  AuthUser,
  Role,
  hasRole as checkHasRole,
} from './auth-types';
import { Permission, hasPermission as checkHasPermission } from './permissions';

/**
 * GraphQL query to fetch current user data from our backend
 * This enriches the WorkOS user with our role/permission data
 */
const GET_CURRENT_USER = gql`
  query GetCurrentUser {
    getUser {
      uuid
      email
      firstName
      lastName
      roles
      status
      isEmailVerified
    }
  }
`;

/**
 * AuthContext value interface
 */
export interface AuthContextValue extends AuthState {
  /** Redirect to WorkOS sign-in */
  signIn: () => void;
  /** Sign out and clear session */
  signOut: () => Promise<void>;
  /** Check if user has a specific role (with hierarchy) */
  hasRole: (role: Role) => boolean;
  /** Check if user has a specific permission */
  hasPermission: (permission: Permission) => boolean;
  /** Get the access token for API calls */
  getAccessToken: () => string | null;
  /** Legacy: Login with email/password - redirects to WorkOS */
  login: (email: string, password: string) => Promise<void>;
  /** Legacy: Login with Google - redirects to WorkOS */
  loginWithGoogle: () => void;
  /** Legacy: Logout */
  logout: () => Promise<void>;
  /** Refresh is handled by WorkOS automatically */
  refreshAccessToken: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderInnerProps {
  children: React.ReactNode;
  onAuthStateChange?: (isAuthenticated: boolean, user: AuthUser | null) => void;
}

/**
 * Inner AuthProvider that uses WorkOS hooks
 * Must be inside AuthKitProvider
 */
function AuthProviderInner({ children, onAuthStateChange }: AuthProviderInnerProps) {
  const {
    user: workosUser,
    isAuthenticated: workosAuthenticated,
    isLoading: workosLoading,
    signIn: workosSignIn,
    signOut: workosSignOut,
    getAccessToken: workosGetAccessToken,
  } = useWorkOSAuth();

  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    accessToken: null,
  });

  // Track if we've ever seen a valid user - prevents race condition from clearing state
  const hasSeenUserRef = useRef(false);

  // Track if we should fetch user data (set by token storage, read by separate effect)
  const [shouldFetchUser, setShouldFetchUser] = useState(false);

  // Fetch our user data when WorkOS is authenticated
  const { loading: userLoading, data: userData, refetch: refetchUser } = useQuery(
    GET_CURRENT_USER,
    {
      fetchPolicy: 'network-only',
      skip: !shouldFetchUser, // Only run query when we have a token stored
    }
  );

  // When WorkOS auth state changes, sync token to localStorage
  useEffect(() => {
    // Check if there's an auth code in the URL - if so, PKCE exchange is in progress
    const hasAuthCode = window.location.search.includes('code=');

    // Note: workosAuthenticated can be undefined even when workosUser is populated (SDK quirk in devMode)
    // So we check for workosUser presence as the primary indicator of successful auth
    if (workosUser) {
      // Mark that we've seen a valid user - prevents race condition from clearing state
      hasSeenUserRef.current = true;

      // Get the WorkOS access token (async) and store it in localStorage
      // so Apollo's auth middleware can use it
      const storeToken = async () => {
        try {
          const accessToken = await workosGetAccessToken?.();
          if (accessToken && typeof accessToken === 'string') {
            localStorage.setItem('token', accessToken);
            setShouldFetchUser(true);
          }
        } catch (error) {
          console.error('[AuthContext] Error getting access token:', error);
        }
      };
      storeToken();
    } else if (!workosLoading && !workosAuthenticated && !workosUser && !hasAuthCode && !hasSeenUserRef.current) {
      // Only clear state if we're truly not in an auth flow AND we haven't seen a user yet
      // This prevents race conditions where the SDK temporarily reports no user
      localStorage.removeItem('token');
      setShouldFetchUser(false);
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        accessToken: null,
      });
    }
  }, [workosAuthenticated, workosUser, workosLoading, workosGetAccessToken]);

  // When our user data is fetched, update state and apply pending partner attribution
  useEffect(() => {
    if (userData?.getUser) {
      console.log('[AuthContext] User data from GraphQL:', userData.getUser);
      setState({
        user: userData.getUser,
        isAuthenticated: true,
        isLoading: false,
        accessToken: localStorage.getItem('token'),
      });

      // Apply pending partner attribution from registration link
      const pendingPartner = sessionStorage.getItem('esig-partner-slug');
      if (pendingPartner && !userData.getUser.partnerSlug) {
        sessionStorage.removeItem('esig-partner-slug');
        // Fire and forget — partner attribution is best-effort
        const graphqlUrl = import.meta.env.VITE_GRAPHQL_URL || '/app';
        fetch(graphqlUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': localStorage.getItem('token') || '',
          },
          body: JSON.stringify({
            query: 'mutation SetPartnerSlug($partnerSlug: String!) { setPartnerSlug(partnerSlug: $partnerSlug) }',
            variables: { partnerSlug: pendingPartner },
          }),
        }).catch(() => {
          // Non-critical — log but don't break auth flow
          console.log('[AuthContext] Partner attribution failed (non-critical)');
        });
      }
    }
  }, [userData, userLoading]);

  // Update loading state
  useEffect(() => {
    setState((prev) => ({
      ...prev,
      isLoading: workosLoading || userLoading,
    }));
  }, [workosLoading, userLoading]);

  /**
   * Sign in - redirects to WorkOS
   */
  const signIn = useCallback(() => {
    workosSignIn();
  }, [workosSignIn]);

  /**
   * Sign out - clears WorkOS session
   */
  const signOut = useCallback(async (): Promise<void> => {
    // Reset the seen user flag so we can clear state properly after logout
    hasSeenUserRef.current = false;
    await workosSignOut();
    localStorage.removeItem('token');
    sessionStorage.removeItem('esig-user-uuid');
    setState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      accessToken: null,
    });
  }, [workosSignOut]);

  /**
   * Check if user has a specific role
   */
  const hasRole = useCallback(
    (role: Role): boolean => {
      if (!state.user) return false;
      return checkHasRole(state.user.roles, role);
    },
    [state.user]
  );

  /**
   * Check if user has a specific permission
   */
  const hasPermission = useCallback(
    (permission: Permission): boolean => {
      if (!state.user) return false;
      return checkHasPermission(state.user.roles, permission);
    },
    [state.user]
  );

  /**
   * Get the current access token
   */
  const getAccessToken = useCallback((): string | null => {
    return workosGetAccessToken?.() || null;
  }, [workosGetAccessToken]);

  /**
   * Legacy login - redirects to WorkOS
   */
  const login = useCallback(
    async (_email: string, _password: string): Promise<void> => {
      // WorkOS handles login - just redirect
      workosSignIn();
    },
    [workosSignIn]
  );

  /**
   * Legacy Google login - redirects to WorkOS
   */
  const loginWithGoogle = useCallback(() => {
    workosSignIn();
  }, [workosSignIn]);

  /**
   * Legacy logout
   */
  const logout = useCallback(async (): Promise<void> => {
    await signOut();
    window.location.href = '/login';
  }, [signOut]);

  /**
   * Refresh is handled by WorkOS automatically
   */
  const refreshAccessToken = useCallback(async (): Promise<boolean> => {
    // WorkOS handles token refresh automatically
    return true;
  }, []);

  /**
   * Notify auth state changes
   */
  useEffect(() => {
    if (onAuthStateChange) {
      onAuthStateChange(state.isAuthenticated, state.user);
    }
  }, [state.isAuthenticated, state.user, onAuthStateChange]);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      signIn,
      signOut,
      hasRole,
      hasPermission,
      getAccessToken,
      login,
      loginWithGoogle,
      logout,
      refreshAccessToken,
    }),
    [
      state,
      signIn,
      signOut,
      hasRole,
      hasPermission,
      getAccessToken,
      login,
      loginWithGoogle,
      logout,
      refreshAccessToken,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

interface AuthProviderProps {
  children: React.ReactNode;
  onAuthStateChange?: (isAuthenticated: boolean, user: AuthUser | null) => void;
}

/**
 * AuthProvider - Wrap your app with this to provide auth context
 *
 * Uses WorkOS AuthKit for authentication.
 *
 * @example
 * ```tsx
 * <AuthProvider>
 *   <App />
 * </AuthProvider>
 * ```
 */
export function AuthProvider({ children, onAuthStateChange }: AuthProviderProps) {
  const clientId = import.meta.env.VITE_WORKOS_CLIENT_ID;
  // Per WorkOS docs: redirect URI should be the same route where auth is required
  // Using /login as the redirect so the auth flow completes on the same page
  const redirectUri = import.meta.env.VITE_WORKOS_REDIRECT_URI || `${window.location.origin}/login`;

  // If WorkOS is not configured, show a message
  if (!clientId) {
    console.warn('WorkOS client ID not configured. Auth will not work.');
    return (
      <AuthContext.Provider
        value={{
          user: null,
          isAuthenticated: false,
          isLoading: false,
          accessToken: null,
          signIn: () => console.warn('WorkOS not configured'),
          signOut: async () => {},
          hasRole: () => false,
          hasPermission: () => false,
          getAccessToken: () => null,
          login: async () => {},
          loginWithGoogle: () => {},
          logout: async () => {},
          refreshAccessToken: async () => false,
        }}
      >
        {children}
      </AuthContext.Provider>
    );
  }

  return (
    <AuthKitProvider
      clientId={clientId}
      redirectUri={redirectUri}
      devMode={true}  // Required for client-only SPA without custom auth domain
    >
      <AuthProviderInner onAuthStateChange={onAuthStateChange}>
        {children}
      </AuthProviderInner>
    </AuthKitProvider>
  );
}

/**
 * useAuth hook - Access auth context in components
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { user, isAuthenticated, hasRole, signOut } = useAuth();
 *
 *   if (!isAuthenticated) {
 *     return <Navigate to="/login" />;
 *   }
 *
 *   return (
 *     <div>
 *       Hello, {user?.firstName}!
 *       {hasRole(Role.ADMIN) && <AdminPanel />}
 *       <button onClick={signOut}>Logout</button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthProvider;
